import { ESdChangeReason, IKuikaStyle, InvisibleStylePart, Visibility } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import classNames from "classnames";
import React from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { ColorInputHelper } from "../../../../shared/utility/color-input-helper";
import { ColorPickerPositionHelper } from "../../../../shared/utility/color-picker-position-helper";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs, { KuikaTabOption } from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import StyleLabel from "./style-label/style-label";
import { ColorPicker } from "./styling-color-picker";
import { styleSectionNeedsRender } from "./styling-window-helpers";

const OUTLINE_STYLE_OPTIONS: KuikaTabOption[] = [
  { value: "none", icon: <KuikaSvg name="x" />, dataTestId: "effects-outline-style-none" },
  {
    value: "solid",
    icon: <KuikaSvg name="line" type="styling-panel-icons" />,
    dataTestId: "effects-outline-style-solid"
  },
  {
    value: "dashed",
    icon: <KuikaSvg name="line-dashed" type="styling-panel-icons" />,
    dataTestId: "effects-outline-style-dashed"
  },
  {
    value: "dotted",
    icon: <KuikaSvg name="line-dotted" type="styling-panel-icons" />,
    dataTestId: "effects-outline-style-dotted"
  }
];

export interface IEffectsOutlineComponentProps {
  form: FormInstance<IKuikaStyle>;
}

interface IEffectsOutlineComponentState {
  isColorPickerActive: boolean;
}

class EffectsOutlineComponent extends PureSdComponent<
  IEffectsOutlineComponentProps & IWithSDProps,
  IEffectsOutlineComponentState
> {
  constructor(props: IEffectsOutlineComponentProps & IWithSDProps) {
    super(props);
    this.state = {
      isColorPickerActive: false
    };
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => styleSectionNeedsRender(changeReason);

  private isEffectsHidden = (): boolean =>
    this.props.sd.getStylesVisibility(InvisibleStylePart.Effects) === Visibility.Hidden;

  private ensureEffectsSection = (fieldsValue: IKuikaStyle): void => {
    const kuikaid: string | undefined = this.props.sd.selectedKuikaIds[0];
    if (!fieldsValue.effects) {
      fieldsValue.effects = { effectsId: null, kuikaid: kuikaid ?? "" };
    }
  };

  private submitForm = (fieldsValue: IKuikaStyle): void => {
    // No resetFields: it re-registers every field and remounts the color picker,
    // dropping caret/focus while typing. fieldsValue is already the full snapshot.
    this.props.form.setFieldsValue(fieldsValue);
    this.props.form.submit();
  };

  handleOutlineStyleChange = (value: string): void => {
    const next: string = (value || "").toLowerCase();
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    this.ensureEffectsSection(fieldsValue);
    if (fieldsValue.effects) {
      fieldsValue.effects.outlineStyle = next;
    }
    this.submitForm(fieldsValue);
  };

  handleOutlineColorChange = (color: string): void => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    this.ensureEffectsSection(fieldsValue);
    if (fieldsValue.effects) {
      fieldsValue.effects.outlineColor = color;
    }
    this.submitForm(fieldsValue);
  };

  handleOutlineColorInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    ColorInputHelper.handleColorInputChange(e.target.value, this.handleOutlineColorChange);
  };

  handleOutlineColorClear = (): void => {
    this.handleOutlineColorChange("");
  };

  toggleColorPicker = (event?: React.MouseEvent<HTMLElement>): void => {
    this.setState({ isColorPickerActive: !this.state.isColorPickerActive });
    if (event) {
      if (typeof event.persist === "function") {
        event.persist();
      }
      const nativeEvent: MouseEvent = event.nativeEvent;
      ColorPickerPositionHelper.saveLastClickEvent(nativeEvent);
      KMainFunctions.setColorTriggeredPickerPosition(nativeEvent);
    }
  };

  closeColorPicker = (): void => {
    this.setState({ isColorPickerActive: false });
  };

  private hasOutlineValue = (field: string): boolean => {
    const value: unknown = this.props.form.getFieldValue(["effects", field]);
    if (value === undefined || value === null || value === "") return false;
    // CSS defaults — "none" outline / "transparent" color is equivalent to no value.
    if (field === "outlineStyle" && value === "none") return false;
    if (field === "outlineColor" && value === "transparent") return false;
    return true;
  };

  private hasAnyOutlineValue = (): boolean => {
    return (
      this.hasOutlineValue("outlineStyle") ||
      this.hasOutlineValue("outlineWidth") ||
      this.hasOutlineValue("outlineColor") ||
      this.hasOutlineValue("outlineOffset")
    );
  };

  private resetOutline = (fields: string[]): void => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    this.ensureEffectsSection(fieldsValue);
    if (fieldsValue.effects) {
      const target: Record<string, unknown> = fieldsValue.effects as unknown as Record<string, unknown>;
      const allFields: string[] = ["outlineStyle", "outlineWidth", "outlineColor", "outlineOffset"];
      const toReset: string[] = fields.length === allFields.length ? allFields : fields;
      toReset.forEach((field: string): void => {
        target[field] = undefined;
      });
    }
    this.submitForm(fieldsValue);
  };

  private isEditingTag = (): boolean => {
    const effectsId: string | null | undefined = this.props.form.getFieldValue(["effects", "effectsId"]);
    return !!(effectsId && effectsId.length > 0 && effectsId !== "-1");
  };

  private getLabelClassName = (): string => {
    const editing: boolean = this.isEditingTag();
    const hasValue: boolean = this.hasAnyOutlineValue();
    return classNames({
      "kuika-styling-panel__label--has-value": editing && hasValue,
      "kuika-styling-panel__label--changed": !editing && hasValue
    });
  };

  render(): React.ReactNode {
    if (this.isEffectsHidden()) {
      return null;
    }

    return (
      <>
        <Form.Item name={["effects", "outlineColor"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["effects", "outlineStyle"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        {/* Width and offset are edited by inputs that only mount while an outline style is set, and
            antd's getFieldsValue() returns only MOUNTED fields. Without these always-mounted twins a
            state switch from a state with no outline (say Focused) back to None rebuilt the base
            from a form that had no width/offset, so the base outline lost both. */}
        <Form.Item name={["effects", "outlineWidth"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>
        <Form.Item name={["effects", "outlineOffset"]} noStyle shouldUpdate>
          <input type="hidden"></input>
        </Form.Item>

        <Form.Item noStyle shouldUpdate>
          {() => {
            const currentStyle: string = (this.props.form.getFieldValue(["effects", "outlineStyle"]) as string) || "";
            return (
              <div className="kuika-styling-panel__form-field-wrapper">
                <StyleLabel
                  fieldPaths={["outlineStyle", "outlineWidth", "outlineColor", "outlineOffset"]}
                  section="outline"
                  hasValue={this.hasAnyOutlineValue()}
                  className={this.getLabelClassName()}
                  onReset={this.resetOutline}
                >
                  Outline
                </StyleLabel>
                <KuikaTabs
                  options={OUTLINE_STYLE_OPTIONS}
                  // Visual-only default: fall back to "none" (the X option) so it shows
                  // selected when no outline is set. Display-only — the store stays empty
                  // and the modified indicator reads the raw value via hasAnyOutlineValue.
                  value={currentStyle || "none"}
                  onChange={this.handleOutlineStyleChange}
                  dataTestId="effects-outline-style-tabs"
                />
              </div>
            );
          }}
        </Form.Item>

        <Form.Item noStyle shouldUpdate>
          {() => {
            const currentStyle: string = (this.props.form.getFieldValue(["effects", "outlineStyle"]) as string) || "";
            const showFields: boolean = currentStyle !== "" && currentStyle !== "none";
            if (!showFields) {
              return null;
            }

            const currentColor: string = (this.props.form.getFieldValue(["effects", "outlineColor"]) as string) || "";

            return (
              <>
                <div className="effects-section__outline-row">
                  <div className="effects-section__outline-row-item">
                    <KuikaSmallText className="effects-section__outline-sub-label">Width</KuikaSmallText>
                    <Form.Item name={["effects", "outlineWidth"]} noStyle>
                      <KuikaInput
                        type="number"
                        size="sm"
                        placeholder="-"
                        rightUnit="px"
                        precision={0}
                        min={0}
                        onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                        data-testid="effects-outline-width-input"
                      />
                    </Form.Item>
                  </div>
                  <div className="effects-section__outline-row-item">
                    <KuikaSmallText className="effects-section__outline-sub-label">Offset</KuikaSmallText>
                    <Form.Item name={["effects", "outlineOffset"]} noStyle>
                      <KuikaInput
                        type="number"
                        size="sm"
                        placeholder="-"
                        rightUnit="px"
                        precision={0}
                        onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
                        data-testid="effects-outline-offset-input"
                      />
                    </Form.Item>
                  </div>
                </div>

                <div className="effects-section__outline-color-row">
                  <KuikaSmallText className="effects-section__outline-sub-label">Color</KuikaSmallText>
                  <div
                    className="kuika-styling-panel__color-input-wrapper kuika-styling-panel__input-wrapper kuika-styling-panel__input-wrapper--full"
                    style={{ gridTemplateColumns: "none" }}
                  >
                    <KuikaInput
                      size="sm"
                      style={{ width: "100%" }}
                      placeholder={"TRANSPARENT"}
                      value={KMainFunctions.convertRgbaToHex(currentColor)}
                      className="kuika-styling-panel__color-input"
                      onClick={(e: React.MouseEvent<HTMLElement>) => this.toggleColorPicker(e)}
                      onChange={this.handleOutlineColorInputChange}
                      data-testid="effects-outline-color-input"
                      leftUnit={
                        currentColor && currentColor !== "transparent" ? (
                          <div
                            onClick={(e: React.MouseEvent<HTMLElement>) => this.toggleColorPicker(e)}
                            style={{
                              width: 24,
                              height: 24,
                              backgroundColor: currentColor
                            }}
                          ></div>
                        ) : (
                          <img
                            src={"/images/styling-panel-icons/transparent.svg"}
                            alt="transparent"
                            className="kuika-styling-panel__transparent-icon"
                            onClick={(e: React.MouseEvent<HTMLElement>) => this.toggleColorPicker(e)}
                          />
                        )
                      }
                      rightUnit={
                        <div
                          className="kuika-styling-panel__color-input-unit"
                          style={{ display: "flex", alignItems: "center", gap: "4px" }}
                        >
                          {currentColor && currentColor !== "transparent" && (
                            <>
                              <KuikaSvg
                                width={16}
                                height={16}
                                name="trash"
                                onClick={this.handleOutlineColorClear}
                                pointer
                              />
                              <KuikaSmallText>HEX</KuikaSmallText>
                            </>
                          )}
                        </div>
                      }
                    />
                  </div>
                </div>
              </>
            );
          }}
        </Form.Item>

        <div
          style={{
            display: this.state.isColorPickerActive ? "block" : "none"
          }}
          className="kuika_colorPicker"
        >
          <Form.Item name={["effects", "outlineColor"]} valuePropName="color" noStyle>
            <ColorPicker
              onCancel={this.closeColorPicker}
              isColorPickerActive={this.state.isColorPickerActive}
              onChange={this.handleOutlineColorChange}
              hideHeaderTabs={true}
            />
          </Form.Item>
        </div>
      </>
    );
  }
}

export default withSD<IEffectsOutlineComponentProps>(EffectsOutlineComponent);
